import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { artifactClient } from '../../lib/connect/clients';
/** First N rows of a data sheet. About is never capped. */ export const DRAFT_PREVIEW_ROW_LIMIT = 100;
/** Files larger than this preview only the first sheet. */ export const DRAFT_PREVIEW_LARGE_BYTES = 10 * 1024 * 1024;
/** ~3 pages of Word body copy. */ export const DRAFT_PREVIEW_DOCX_BLOCKS = 45;
const WORD_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const DRAW_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';
const ABOUT_SHEET = /^about$/i;
const HEADING_STYLE = /^(Title|Heading[1-6])$/i;
/** True when `contents` is inlined Office (ZIP) bytes, not the card's summary line. */ export function isInlineOfficeBytes(contents) {
    if (!contents) return false;
    const compact = contents.replace(/\s+/g, '');
    if (compact.length < 8) return false;
    try {
        const head = Uint8Array.from(atob(compact.slice(0, 8)), (ch)=>ch.charCodeAt(0));
        return head[0] === 0x50 && head[1] === 0x4b;
    } catch  {
        return false;
    }
}
export function decodeBase64Bytes(contents) {
    const compact = contents.replace(/\s+/g, '');
    const binary = atob(compact);
    const bytes = new Uint8Array(binary.length);
    for(let i = 0; i < binary.length; i++)bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
}
export function encodeBase64Bytes(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunk = 0x8000;
    for(let i = 0; i < bytes.length; i += chunk){
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
}
/**
 * Resolve draft file bytes. Inline `contents` wins when it is Office base64;
 * otherwise the hidden owner-only tesoro draft is fetched the same way
 * attachments are (GenerateDownloadUrl, then GET).
 */ export async function loadDraftBytes(source) {
    if (isInlineOfficeBytes(source.contents)) {
        return decodeBase64Bytes(source.contents);
    }
    if (!source.draftArtifactId) {
        throw new Error('no draft bytes');
    }
    const dl = await artifactClient.generateDownloadUrl({
        artifactId: source.draftArtifactId
    });
    if (!dl.url) {
        throw new Error('No download URL returned for artifact');
    }
    const url = new URL(dl.url, window.location.href);
    const downloadUrl = url.pathname.startsWith('/storage/v1/artifacts/') ? `${url.pathname}${url.search}` : dl.url;
    const response = await fetch(downloadUrl);
    if (!response.ok) {
        throw new Error(`Failed to fetch draft: ${response.statusText}`);
    }
    return response.arrayBuffer();
}
export function previewFirstSheetOnly(source, bytes) {
    const size = source.sizeBytes && source.sizeBytes > 0 ? source.sizeBytes : bytes.byteLength;
    return size > DRAFT_PREVIEW_LARGE_BYTES;
}
export async function parseDraftOffice(buffer, documentType, options) {
    if (documentType === 'xlsx') return parseDraftWorkbook(buffer, options);
    if (documentType === 'docx') return parseDraftDocument(buffer);
    return parseDraftDeck(buffer);
}
function formatDraftDate(date) {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}
function formatCellValue(value) {
    if (value === null || value === undefined) return '';
    if (value instanceof Date) return formatDraftDate(value);
    if (typeof value === 'object') {
        const obj = value;
        if ('result' in obj && obj.result !== undefined) return formatCellValue(obj.result);
        if (Array.isArray(obj.richText)) return obj.richText.map((run)=>run.text ?? '').join('');
        if (typeof obj.text === 'string' && obj.text) return obj.text;
        if (typeof obj.hyperlink === 'string') return obj.hyperlink;
        return '';
    }
    return String(value);
}
function cssFromArgb(argb) {
    const hex = argb.length === 8 ? argb.slice(2) : argb;
    if (!hex || hex === '00000000') return undefined;
    return `#${hex}`;
}
function fontColor(cell) {
    const argb = cell.font?.color?.argb;
    return argb ? cssFromArgb(argb) : undefined;
}
function cellFill(cell) {
    const fill = cell.fill;
    if (!fill || fill.type !== 'pattern') return undefined;
    const argb = fill.fgColor?.argb;
    return argb ? cssFromArgb(argb) : undefined;
}
async function parseDraftWorkbook(buffer, options) {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const sheets = [];
    workbook.eachSheet((worksheet)=>{
        if (options?.firstSheetOnly && sheets.length > 0) return;
        const sheetRows = worksheet.rowCount;
        const colCount = worksheet.columnCount;
        const about = ABOUT_SHEET.test(worksheet.name);
        let frozenRows = 0;
        const view = worksheet.views?.[0];
        if (view?.state === 'frozen') frozenRows = view.ySplit ?? 0;
        // Frozen rows are the header: never counted as data, always shown.
        const headerRows = Math.min(frozenRows, sheetRows);
        const totalRows = sheetRows - headerRows;
        const keep = about ? sheetRows : Math.min(sheetRows, headerRows + DRAFT_PREVIEW_ROW_LIMIT);
        const rows = [];
        const styles = [];
        for(let r = 1; r <= keep; r++){
            const wsRow = worksheet.getRow(r);
            const row = [];
            const rowStyles = [];
            for(let c = 1; c <= colCount; c++){
                const cell = wsRow.getCell(c);
                row.push(formatCellValue(cell.value));
                rowStyles.push({
                    fill: cellFill(cell),
                    bold: Boolean(cell.font?.bold),
                    color: fontColor(cell)
                });
            }
            rows.push(row);
            styles.push(rowStyles);
        }
        const colWidths = [];
        for(let c = 1; c <= colCount; c++){
            const width = worksheet.getColumn(c).width ?? 8.43;
            colWidths.push(Math.round(width * 7));
        }
        sheets.push({
            name: worksheet.name,
            rows,
            totalRows,
            truncated: !about && totalRows > DRAFT_PREVIEW_ROW_LIMIT,
            colWidths,
            frozenRows,
            styles
        });
    });
    if (sheets.length === 0) throw new Error('workbook has no sheets');
    return {
        kind: 'xlsx',
        sheets
    };
}
function paragraphText(paragraph) {
    let line = '';
    for (const node of Array.from(paragraph.getElementsByTagNameNS(WORD_NS, '*'))){
        if (node.localName === 't') line += node.textContent ?? '';
        else if (node.localName === 'tab') line += '\t';
        else if (node.localName === 'br') line += ' ';
    }
    return line.trim();
}
function paragraphStyle(paragraph) {
    const style = paragraph.getElementsByTagNameNS(WORD_NS, 'pStyle')[0];
    return style?.getAttributeNS(WORD_NS, 'val') ?? style?.getAttribute('w:val') ?? undefined;
}
async function parseDraftDocument(buffer) {
    const entry = (await JSZip.loadAsync(buffer)).file('word/document.xml');
    if (!entry) throw new Error('not a Word document');
    const xml = new DOMParser().parseFromString(await entry.async('string'), 'application/xml');
    const blocks = [];
    for (const paragraph of Array.from(xml.getElementsByTagNameNS(WORD_NS, 'p'))){
        const text = paragraphText(paragraph);
        if (!text) continue;
        const style = paragraphStyle(paragraph);
        blocks.push({
            type: style && HEADING_STYLE.test(style) ? 'heading' : 'paragraph',
            text
        });
    }
    const truncated = blocks.length > DRAFT_PREVIEW_DOCX_BLOCKS;
    return {
        kind: 'docx',
        blocks: blocks.slice(0, DRAFT_PREVIEW_DOCX_BLOCKS),
        truncated,
        totalBlocks: blocks.length
    };
}
function slideTexts(xml) {
    const texts = Array.from(xml.getElementsByTagNameNS(DRAW_NS, 't')).map((node)=>(node.textContent ?? '').trim()).filter(Boolean);
    return {
        title: texts[0] ?? 'Slide',
        bullets: texts.slice(1)
    };
}
async function parseDraftDeck(buffer) {
    const zip = await JSZip.loadAsync(buffer);
    const names = Object.keys(zip.files).filter((name)=>/^ppt\/slides\/slide\d+\.xml$/i.test(name)).sort((a, b)=>{
        const left = Number(a.match(/slide(\d+)/i)?.[1] ?? 0);
        const right = Number(b.match(/slide(\d+)/i)?.[1] ?? 0);
        return left - right;
    });
    if (names.length === 0) throw new Error('not a PowerPoint deck');
    const slides = [];
    for (const name of names){
        const entry = zip.file(name);
        if (!entry) continue;
        const xml = new DOMParser().parseFromString(await entry.async('string'), 'application/xml');
        slides.push(slideTexts(xml));
    }
    return {
        kind: 'pptx',
        slides
    };
}
